import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listIncomeLedgers, listCashOnlyLedgers } from "../../api/accountApi";
import {
  listFeeHeadLedgers,
  saveFeeHeadLedger,
  deleteFeeHeadLedger,
  listFeeFineLedgers,
  saveFeeFineLedger,
  deleteFeeFineLedger,
  listAutoLedgerMappings,
  saveAutoLedgerMapping,
  deleteAutoLedgerMapping,
} from "../../api/feeApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const feeHeadApi = createLookupApi("fee-heads");

/**
 * Which income ledger each fee head's regular collections / fines post to,
 * plus the per-class-configuration fallback ledger — three old
 * digicampusbd pages (Fee Ledger Setup, Fee Fine Setup, Auto Ledger Setup)
 * folded into one, tab-switched (mirrors FeeLedgerConfigService).
 *
 * The ledger picker on each tab is scoped to the real old app's own real
 * category, not every ledger in the institute (a real, confirmed deviation,
 * found while sweeping Fee Management — see MIGRATION.md): Fee Head/Fee Fine
 * Ledger Setup's real `/general/accounts/report/income/ledger/list` only
 * offers Income-category ledgers; Auto Ledger Setup's real
 * `/general/accounts/report/cash-equivalance/ledger/list` (`GeneralAccountReportService.
 * cashEquiValanceLedgerList`, category_default_id {102} only — NOT the wider
 * {102,103} `listCashEquivalentLedgers`, a different endpoint/method despite the
 * similar name) only offers Cash & Cash Equivalent ledgers, no Account
 * Receivable — the same 102-only scope already used correctly by the
 * fee-collection "Paid By" picker (`listCashOnlyLedgers`) elsewhere in this
 * migration.
 */
export default function FeeLedgerSetupPage() {
  const [tab, setTab] = useState("fee-head");
  const [feeHeads, setFeeHeads] = useState([]);
  const [incomeLedgers, setIncomeLedgers] = useState([]);
  const [cashOnlyLedgers, setCashOnlyLedgers] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([feeHeadApi.list(), listIncomeLedgers(), listCashOnlyLedgers(), listClassConfigurations()]).then(
      ([heads, incomeLedgerList, cashOnlyLedgerList, configs]) => {
        setFeeHeads(heads);
        setIncomeLedgers(incomeLedgerList);
        setCashOnlyLedgers(cashOnlyLedgerList);
        setClassConfigs(configs);
      }
    );
  }, []);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Ledger Setup</h1>
      </div>
      <Tabs
        tabs={[
          { key: "fee-head", label: "Fee Head Ledger" },
          { key: "fee-fine", label: "Fee Fine Ledger" },
          { key: "auto-ledger", label: "Auto Ledger Setup" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "fee-head" && (
        <MappingTab
          feeHeads={feeHeads}
          ledgers={incomeLedgers}
          list={listFeeHeadLedgers}
          save={saveFeeHeadLedger}
          remove={deleteFeeHeadLedger}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
      {tab === "fee-fine" && (
        <MappingTab
          feeHeads={feeHeads}
          ledgers={incomeLedgers}
          list={listFeeFineLedgers}
          save={saveFeeFineLedger}
          remove={deleteFeeFineLedger}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
      {tab === "auto-ledger" && (
        <AutoLedgerTab classConfigs={classConfigs} ledgers={cashOnlyLedgers} setError={setError} setSuccess={setSuccess} />
      )}
    </MainLayout>
  );
}

/** Shared by the fee-head-ledger and fee-fine-ledger tabs — same shape, different endpoint. */
function MappingTab({ feeHeads, ledgers, list, save, remove, setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [ledgerId, setLedgerId] = useState("");

  const load = () => {
    setLoading(true);
    list()
      .then(setRows)
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await save({ feeHeadId: Number(feeHeadId), ledgerId: Number(ledgerId) });
      setSuccess("Mapping saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ledger mapping for "${row.feeHeadName}"?`)) return;
    try {
      await remove(row.configId);
      setSuccess("Removed.");
      setRows((prev) => prev.filter((r) => r.configId !== row.configId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <div className="attendance-filters">
          <label>
            Fee Head
            <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)} required>
              <option value="" disabled>
                Select
              </option>
              {feeHeads.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ledger
            <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)} required>
              <option value="" disabled>
                Select
              </option>
              {ledgers.map((l) => (
                <option key={l.ledgerId} value={l.ledgerId}>
                  {l.ledgerName}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save (creates or updates)
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "feeHeadName", label: "Fee Head" },
            { key: "ledgerName", label: "Ledger" },
          ]}
          rows={rows}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Remove
            </button>
          )}
        />
      )}
    </>
  );
}

function AutoLedgerTab({ classConfigs, ledgers, setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [ledgerId, setLedgerId] = useState("");

  const load = () => {
    setLoading(true);
    listAutoLedgerMappings()
      .then(setRows)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await saveAutoLedgerMapping({ classConfigurationId: Number(classConfigurationId), ledgerId: Number(ledgerId) });
      setSuccess("Mapping saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove auto-ledger mapping for "${row.classConfigName}"?`)) return;
    try {
      await deleteAutoLedgerMapping(row.setupId);
      setSuccess("Removed.");
      setRows((prev) => prev.filter((r) => r.setupId !== row.setupId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <div className="attendance-filters">
          <label>
            Class Configuration
            <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)} required>
              <option value="" disabled>
                Select
              </option>
              {classConfigs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.classConfigName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ledger
            <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)} required>
              <option value="" disabled>
                Select
              </option>
              {ledgers.map((l) => (
                <option key={l.ledgerId} value={l.ledgerId}>
                  {l.ledgerName}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save (creates or updates)
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "classConfigName", label: "Class Configuration" },
            { key: "ledgerName", label: "Ledger" },
          ]}
          rows={rows}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Remove
            </button>
          )}
        />
      )}
    </>
  );
}
